import React, { useState, useCallback, useRef } from 'react'
import { Grid } from '@material-ui/core'
import { useParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import { useGetComponentBlueprint, useListComponentBlueprintVersions } from '../../../../api/denali'
import Loading from '../../../molecules/Loading'
import AdminPage from '../../admin/shared/AdminPage'
import ErrorDisplay from '../../../molecules/ErrorDisplay'
import SydTabs from '../../../commonDesign/SydTabs'
import FlexRow from '../../../molecules/FlexRow'
import ComponentsTab from './components/ComponentsTab'
import SettingsTab from './components/SettingsTab'
import VersionHistoryTab from './components/VersionHistoryTab'
import NewVersionDialog from './components/NewVersionDialog'
import SetActiveVersionDialog from './components/SetActiveVersionDialog'
import DeleteVersionDialog from './components/DeleteVersionDialog'

const tabs = {
  components: {
    label: 'Components',
    value: 'components'
  },
  history: {
    label: 'Version History',
    value: 'history'
  },
  settings: {
    label: 'Settings',
    value: 'settings'
  }
}

function ComponentBlueprintDetails ({ root }) {
  const { blueprintId } = useParams()
  const [currentTab, setCurrentTab] = useState('components')
  const addDefinitionRef = useRef(null)
  const setActiveVersionRef = useRef(null)
  const deleteVersionRef = useRef(null)

  const { data: blueprint, isLoading, error, refetch } = useGetComponentBlueprint(
    parseInt(blueprintId),
    { includes: { definition: true } }
  )

  const { data: versions, refetch: refetchVersions } = useListComponentBlueprintVersions(
    parseInt(blueprintId)
  )

  const handleAddDefinition = useCallback(() => {
    addDefinitionRef.current.open()
  }, [])

  const handleSetActiveVersion = useCallback((version) => {
    setActiveVersionRef.current.open(version)
  }, [])

  const handleDeleteVersion = useCallback((version) => {
    deleteVersionRef.current.open(version)
  }, [])

  const handleDefinitionComplete = useCallback(() => {
    refetch()
    refetchVersions()
  }, [refetch, refetchVersions])

  const handleDeleteComplete = useCallback(() => {
    refetchVersions()
    refetch()
  }, [refetchVersions, refetch])

  if (error) {
    return (
      <AdminPage>
        <ErrorDisplay error={error} onRetry={refetch} />
      </AdminPage>
    )
  }

  if (isLoading || !blueprint) {
    return (
      <AdminPage>
        <Loading />
      </AdminPage>
    )
  }

  return (
    <AdminPage>
      <SydTabs.Group
        tabOptions={tabs}
        onChange={setCurrentTab}
        defaultValue={currentTab}
        key='component_blueprint_details_tabs'
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FlexRow className='justify-between' style={{ alignItems: 'center', marginBottom: '16px' }}>
              <h1>[Component Blueprint] {blueprint.name}</h1>
              <SydTabs.MiniList />
            </FlexRow>
          </Grid>

          <Grid item xs={12}>
            <SydTabs.Tab value='components'>
              <ComponentsTab
                blueprint={blueprint}
                onSave={handleDefinitionComplete}
              />
            </SydTabs.Tab>

            <SydTabs.Tab value='history'>
              <VersionHistoryTab
                blueprint={blueprint}
                versions={versions || []}
                onSetActive={handleSetActiveVersion}
                onDeleteVersion={handleDeleteVersion}
              />
            </SydTabs.Tab>

            <SydTabs.Tab value='settings'>
              <SettingsTab
                blueprint={blueprint}
                onAddDefinition={handleAddDefinition}
              />
            </SydTabs.Tab>
          </Grid>
        </Grid>
      </SydTabs.Group>

      <NewVersionDialog
        ref={addDefinitionRef}
        blueprint={blueprint}
        allVersions={versions || []}
        onComplete={handleDefinitionComplete}
      />
      <SetActiveVersionDialog
        ref={setActiveVersionRef}
        blueprint={blueprint}
        onComplete={handleDefinitionComplete}
      />
      <DeleteVersionDialog
        ref={deleteVersionRef}
        blueprint={blueprint}
        onComplete={handleDeleteComplete}
      />
    </AdminPage>
  )
}

ComponentBlueprintDetails.propTypes = {
  root: PropTypes.any
}

export default ComponentBlueprintDetails
